/* Billetes: un mazo que se pasa con el dedo y se da la vuelta para ver el localizador.
   «Todos» es una lista por días: 60 billetes de uno en uno no se pueden recorrer. */
import { useRef, useState } from 'react';
import type { Reserva, Viaje } from '../datos/tipos.ts';
import { Hoja } from '../componentes/Hoja.tsx';
import { fechaCorta, num2, titulo } from '../texto.ts';
import { CaraKippu, estadoRes, SELLO, tieneLoc } from './kippu.tsx';

type Filtro = 'dia' | 'proximos' | 'pendientes' | 'todos';

export function HojaBilletes({ v, n, hoy, onCerrada }: { v: Viaje; n: number; hoy: number; onCerrada: () => void }) {
  const hayDelDia = v.reservas.some(r => r.dia === n && !/CANCEL/.test(r.estado ?? ''));
  const [filtro, setFiltro] = useState<Filtro>(hayDelDia ? 'dia' : 'proximos');
  const [i, setI] = useState(0);
  const [volteado, setVolteado] = useState(false);
  const [arrastre, setArrastre] = useState<number | null>(null);
  const inicio = useRef<number | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);
  const [canceladas, setCanceladas] = useState(false);

  const cumple: Record<Filtro, (r: Viaje['reservas'][number]) => boolean> = {
    dia: r => r.dia === n && !/CANCEL/.test(r.estado ?? ''),
    proximos: r => !/CANCEL/.test(r.estado ?? '') && (r.dia ?? 99) >= Math.max(1, hoy),
    pendientes: r => /PENDIENTE/.test(r.estado ?? ''),
    todos: r => canceladas || !/CANCEL/.test(r.estado ?? ''),
  };
  const lista = v.reservas.filter(cumple[filtro])
    .sort((a, b) => (a.dia ?? 0) - (b.dia ?? 0) || String(a.inicio ?? '').localeCompare(String(b.inicio ?? '')));

  const copiar = (r: Reserva) => { navigator.clipboard?.writeText(r.localizador!).catch(() => {}); setCopiado(r.id); setTimeout(() => setCopiado(null), 1500); };
  const mover = (dir: number) => { setI(x => Math.max(0, Math.min(lista.length - 1, x + dir))); setVolteado(false); };
  const elegir = (f: Filtro) => { setFiltro(f); setI(0); setVolteado(false); };

  return (
    <Hoja id="hoja-billetes" titulo="billetes-titulo" onCerrada={onCerrada}>
      {() => (
        <>
          <p className="hoja__kicker">切符 · RESERVAS Y LOCALIZADORES</p>
          <h2 id="billetes-titulo">Billetes</h2>
          <div className="filtros" role="group" aria-label="Qué billetes ver">
            {([['dia', `Día ${n}`], ['proximos', 'Próximos'], ['pendientes', 'Por cerrar'], ['todos', 'Todos']] as [Filtro, string][]).map(([k, t]) => (
              <button key={k} type="button" aria-pressed={k === filtro} onClick={() => elegir(k)}>{t}</button>
            ))}
          </div>
          {filtro === 'todos' ? <ListaBilletes v={v} lista={lista} copiar={copiar} copiado={copiado} canceladas={canceladas} setCanceladas={setCanceladas} /> : <>
          <div className="mazo">
            {lista.length === 0 && <p className="texto">Nada por aquí.</p>}
            {lista.map((r, k) => {
              const d = k - i, arriba = d === 0;
              const giro = arriba && volteado ? ' rotateY(180deg)' : '';
              const estilo = d < 0
                ? { transform: 'translateX(-130%) rotate(-16deg)', opacity: 0, zIndex: 0 }
                : arriba && arrastre !== null
                  ? { transform: `translateX(${arrastre}px) rotate(${arrastre / 18}deg)${giro}`, transition: 'none', zIndex: 100 }
                  : { transform: `translateY(${d * 12}px) scale(${1 - d * 0.05}) rotate(${d ? (d % 2 ? 2.5 : -2.5) : 0}deg)${giro}`, opacity: d > 2 ? 0 : 1, zIndex: 100 - d };
              return (
                <div
                  key={r.id}
                  className={`kippu ${arriba && volteado ? 'volteado' : ''}`}
                  data-estado={estadoRes(r)}
                  role="button"
                  tabIndex={arriba ? 0 : -1}
                  aria-hidden={!arriba}
                  aria-label={`${r.que}. Toca para ver el localizador`}
                  style={estilo}
                  onPointerDown={e => { if (!arriba || (e.target as HTMLElement).closest('button')) return; inicio.current = e.clientX; setArrastre(0); (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); }}
                  onPointerMove={e => { if (inicio.current !== null) setArrastre(e.clientX - inicio.current); }}
                  onPointerUp={() => {
                    if (inicio.current === null) return;
                    const dx = arrastre ?? 0;
                    inicio.current = null; setArrastre(null);
                    if (Math.abs(dx) > 80) mover(dx < 0 ? 1 : -1);
                    else if (Math.abs(dx) < 6) setVolteado(x => !x);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setVolteado(x => !x); }
                    if (e.key === 'ArrowRight') mover(1);
                    if (e.key === 'ArrowLeft') mover(-1);
                  }}
                >
                  <CaraKippu v={v} r={r} />
                  <div className="kippu__cara kippu__cara--atras">
                    <p className="kippu__cab"><span>LOCALIZADOR</span></p>
                    <p className="kippu__loc">{tieneLoc(r) ? r.localizador : 'Sin localizador'}</p>
                    {tieneLoc(r) && (
                      <button type="button" onClick={() => copiar(r)}>
                        {copiado === r.id ? 'Copiado ✓' : 'Copiar'}
                      </button>
                    )}
                    <p className="kippu__nota">{r.notas ?? ''}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mazo-pie">
            <button type="button" aria-label="Billete anterior" onClick={() => mover(-1)}>‹</button>
            <span aria-live="polite">{lista.length ? `${i + 1} / ${lista.length}` : '0'}</span>
            <button type="button" aria-label="Billete siguiente" onClick={() => mover(1)}>›</button>
          </div>
          <p className="texto" style={{ fontSize: '.85rem', textAlign: 'center' }}>Toca el billete para darle la vuelta · desliza para pasar</p>
          </>}
        </>
      )}
    </Hoja>
  );
}

interface PropsLista { v: Viaje; lista: Reserva[]; copiar: (r: Reserva) => void; copiado: string | null; canceladas: boolean; setCanceladas: (x: boolean) => void }

/** Todos los billetes, agrupados por día; cada uno se abre para ver su localizador. */
function ListaBilletes({ v, lista, copiar, copiado, canceladas, setCanceladas }: PropsLista) {
  const grupos = new Map<number, Reserva[]>();
  for (const r of lista) grupos.set(r.dia ?? 0, [...(grupos.get(r.dia ?? 0) ?? []), r]);
  return (
    <div className="billetes-lista">
      <label className="interruptor"><input type="checkbox" checked={canceladas} onChange={e => setCanceladas(e.target.checked)} /> Ver también las canceladas</label>
      {[...grupos].sort((a, b) => a[0] - b[0]).map(([n, rs]) => {
        const d = v.dias[n - 1];
        return (
          <section key={n}>
            <h3>{d ? `JP${num2(d.n)} · ${fechaCorta(d)}` : 'Todo el viaje'}</h3>
            {rs.map(r => (
              <details key={r.id} className="billete" data-estado={estadoRes(r)}>
                <summary>
                  <time>{r.inicio && r.inicio !== '—' ? r.inicio.replace(' aprox.', '') : '—'}</time>
                  <span className="que">{titulo(r.que)}</span>
                  <span className="sello" lang="ja" aria-label={r.estado ?? ''}>{SELLO[estadoRes(r)]}</span>
                </summary>
                <div>
                  <p>{r.estado?.toLowerCase()}{r.plataforma ? ` · ${r.plataforma}` : ''}</p>
                  {tieneLoc(r)
                    ? <p className="billete__loc"><b>{r.localizador}</b> <button type="button" className="enlace-texto" onClick={() => copiar(r)}>{copiado === r.id ? 'Copiado ✓' : 'Copiar'}</button></p>
                    : <p>Sin localizador</p>}
                  {r.notas && <p className="billete__nota">{r.notas}</p>}
                </div>
              </details>
            ))}
          </section>
        );
      })}
    </div>
  );
}
